import {faDiscord, faGithub, faPatreon} from '@fortawesome/free-brands-svg-icons';
import {
	faClock,
	faCogs,
	faDonate,
	faHeart,
	faPlusSquare,
	faSearch,
	faSave,
	faShieldAlt,
	faSignInAlt,
	faSignOutAlt,
	faTrophy,
	faUser,
	faWrench,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Link} from '@tanstack/react-router';
import {getAuth, signOut, type User} from 'firebase/auth';
import type React from 'react';
import {useState} from 'react';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import {useAuthState} from 'react-firebase-hooks/auth';

import {app} from '../base';
import {useIsModerator} from '../hooks/useModerators';
import {AuthenticationForm} from './auth/AuthenticationForm';

const Header: React.FC = () => {
	const [user] = useAuthState(getAuth(app)) as [User | null | undefined, boolean, Error | undefined];
	const moderatorQuery = useIsModerator(user?.uid);
	const isModerator = moderatorQuery.data ?? false;
	const [showAccountDropdown, setShowAccountDropdown] = useState<boolean>(false);

	const handleLogout = (): void => {
		signOut(getAuth(app));
	};

	const getDisplayName = (): React.ReactElement | false => {
		if (user && user.displayName) {
			return (
				<div className="h4">
					<b>{user.displayName}</b>
				</div>
			);
		}
		return false;
	};

	const renderAuthentication = (): React.ReactElement => {
		if (user) {
			const closeDropdown = () => setShowAccountDropdown(false);

			return (
				<Dropdown
					className="text-light"
					show={showAccountDropdown}
					onToggle={(isOpen) => setShowAccountDropdown(isOpen)}
				>
					<Dropdown.Toggle variant="link">Account Settings</Dropdown.Toggle>
					<Dropdown.Menu className="dropdown-menu-right text-left">
						<Dropdown.Item
							as="div"
							className="user-photo-container"
						>
							{getDisplayName()}
						</Dropdown.Item>
						<Dropdown.Item
							as={Link}
							to="/favorites"
							onClick={closeDropdown}
							className="text-light text-left"
						>
							<FontAwesomeIcon
								icon={faHeart}
								size="lg"
								fixedWidth
							/>
							{' My Favorites'}
						</Dropdown.Item>
						<Dropdown.Item
							as={Link}
							to="/collection"
							onClick={closeDropdown}
							className="text-light text-left"
						>
							<FontAwesomeIcon
								icon={faSave}
								size="lg"
								fixedWidth
							/>
							{' My Collection'}
						</Dropdown.Item>
						<Dropdown.Item
							as="div"
							onClick={closeDropdown}
							className="text-light text-left p-0"
						>
							<Link
								to="/user/$userId"
								params={{userId: user!.uid}}
								className="dropdown-item text-light text-left"
							>
								<FontAwesomeIcon
									icon={faUser}
									size="lg"
									fixedWidth
								/>
								{' My Blueprints'}
							</Link>
						</Dropdown.Item>
						<Dropdown.Item
							as={Link}
							to="/account"
							onClick={closeDropdown}
							className="text-light text-left"
						>
							<FontAwesomeIcon
								icon={faWrench}
								size="lg"
								fixedWidth
								style={{color: 'var(--bs-orange)'}}
							/>
							{' My Display Name'}
						</Dropdown.Item>
						{isModerator && (
							<Dropdown.Item
								as={Link}
								to="/users"
								onClick={closeDropdown}
								className="text-light text-left"
							>
								<FontAwesomeIcon
									icon={faShieldAlt}
									size="lg"
									fixedWidth
									style={{color: 'var(--bs-purple)'}}
								/>
								{' Admin: Users'}
							</Dropdown.Item>
						)}
						<Dropdown.Item>
							<Button
								className="w-100"
								variant="warning"
								size="lg"
								onClick={() => {
									handleLogout();
									closeDropdown();
								}}
							>
								<FontAwesomeIcon
									icon={faSignOutAlt}
									size="lg"
									fixedWidth
								/>
								{' Log out'}
							</Button>
						</Dropdown.Item>
					</Dropdown.Menu>
				</Dropdown>
			);
		}

		const title = (
			<span className="text-light">
				<FontAwesomeIcon
					icon={faSignInAlt}
					size="lg"
					fixedWidth
				/>
				{' Sign in / Join'}
			</span>
		);

		return (
			<NavDropdown
				title={title}
				style={{minWidth: '210px'}}
			>
				<div className="p-2">
					<AuthenticationForm showDivider={false} />
				</div>
			</NavDropdown>
		);
	};

	return (
		<Navbar
			expand="lg"
			sticky="top"
			collapseOnSelect
			bg="warning"
		>
			<Navbar.Brand>
				<Link to="/">
					<FontAwesomeIcon
						icon={faCogs}
						size="lg"
						fixedWidth
					/>
					{' Factorio Prints'}
				</Link>
			</Navbar.Brand>
			<Navbar.Toggle />

			<Navbar.Collapse>
				<Nav className="mr-auto">
					<Nav.Item>
						<Link
							to="/search"
							className="nav-link text-light"
						>
							<FontAwesomeIcon
								icon={faSearch}
								size="lg"
								fixedWidth
							/>
							{' Search'}
						</Link>
					</Nav.Item>
					<Nav.Item>
						<Link
							to="/blueprints"
							className="nav-link text-light"
						>
							<FontAwesomeIcon
								icon={faClock}
								size="lg"
								fixedWidth
							/>
							{' Most Recent'}
						</Link>
					</Nav.Item>
					<Nav.Item>
						<Link
							to="/top"
							className="nav-link text-light"
						>
							<FontAwesomeIcon
								icon={faTrophy}
								size="lg"
								fixedWidth
							/>
							{' Most Favorited'}
						</Link>
					</Nav.Item>
					<Nav.Item>
						<Link
							to="/collection"
							className="nav-link text-light"
						>
							<FontAwesomeIcon
								icon={faSave}
								size="lg"
								fixedWidth
							/>
							{' Your Collection'}
						</Link>
					</Nav.Item>
					<NavDropdown
						title={
							<span>
								<FontAwesomeIcon
									icon={faPlusSquare}
									size="lg"
									fixedWidth
								/>
								{' Create'}
							</span>
						}
						id="create-dropdown"
						className="nav-item-dropdown"
					>
						<NavDropdown.Item
							as={Link}
							to="/create"
						>
							Create Blueprint
						</NavDropdown.Item>
						<NavDropdown.Divider />
						<NavDropdown.Item
							as={Link}
							to="/import/factorio-bin"
						>
							Import from Factorio Bin
						</NavDropdown.Item>
					</NavDropdown>
					<Nav.Item>
						<Link
							to="/chat"
							className="nav-link text-light"
						>
							<FontAwesomeIcon
								icon={faDiscord}
								size="lg"
								fixedWidth
							/>
							{' Chat'}
						</Link>
					</Nav.Item>
					<Nav.Item>
						<Link
							to="/contributors"
							className="nav-link text-light"
						>
							<FontAwesomeIcon
								icon={faPatreon}
								size="lg"
								fixedWidth
							/>
							{' Contributors'}
						</Link>
					</Nav.Item>
					<Nav.Link
						href="https://github.com/FactorioBlueprints/factorio-prints"
						className="text-light"
						target="_blank"
						rel="noopener noreferrer"
					>
						<FontAwesomeIcon
							icon={faGithub}
							size="lg"
							fixedWidth
						/>
						{' GitHub'}
					</Nav.Link>
					<Nav.Link
						href="https://www.patreon.com/FactorioBlueprints"
						className="text-dark"
						target="_blank"
						rel="noopener noreferrer"
					>
						<FontAwesomeIcon
							icon={faDonate}
							size="lg"
							fixedWidth
						/>
						{' Donate'}
					</Nav.Link>
				</Nav>
				<Nav
					className="mr-sm-2"
					justify
				>
					{renderAuthentication()}
				</Nav>
			</Navbar.Collapse>
		</Navbar>
	);
};

export default Header;
